import React, { useMemo } from 'react';
import {
  Alert,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Model, surveyLocalization, SurveyModel } from 'survey-core';
import { Survey as SurveyComponent } from 'survey-react-ui';
import { PlainLightPanelless } from 'survey-core/themes';
import 'survey-core/i18n/french';
import { observer } from 'mobx-react';
import { IconCheck, IconClock, IconDownload } from '@tabler/icons-react';
import { useGetMySignedEICFConsent } from '../../../global/entities/EICFConsents/queries/useGetMySignedEICFConsent.tsx';
import { useDownloadMySignedEICFConsentPdf } from '../../../global/entities/EICFConsents/mutations/useDownloadMySignedEICFConsentPdf.tsx';
import {
  EICFConsentStatus,
  EICFConsentTemplateKind,
} from '../../../global/entities/EICFConsents/EICFConsent.ts';

surveyLocalization.supportedLocales = ['en', 'fr'];

const EICFConsentViewPage: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const { data: consent, isLoading } = useGetMySignedEICFConsent(true);
  const downloadPdf = useDownloadMySignedEICFConsentPdf();

  // The patient only re-reads a consent they already signed; always render the
  // submitted answers in display mode with the survey's complete button hidden.
  const survey: SurveyModel | null = useMemo(() => {
    if (!consent?.templateRawJson) return null;
    try {
      const model = new Model(consent.templateRawJson);
      model.applyTheme(PlainLightPanelless);
      model.locale = i18n.language;
      if (consent.answersJson) {
        try {
          model.data = JSON.parse(consent.answersJson);
        } catch (e) {
          console.error('Failed to parse answersJson', e);
        }
      }
      model.mode = 'display';
      model.showCompleteButton = false;
      return model;
    } catch (e) {
      console.error('Failed to load consent survey JSON', e);
      return null;
    }
  }, [consent, i18n.language]);

  if (isLoading) {
    return (
      <Container py="xl">
        <Loader />
      </Container>
    );
  }

  if (!consent) {
    return (
      <Container py="xl">
        <Alert
          icon={<IconCheck size="1rem" />}
          color="blue"
          title={t('eicf.view.noConsentTitle')}
        >
          {t('eicf.view.noConsentBody')}
        </Alert>
      </Container>
    );
  }

  const isFullySigned = consent.status === EICFConsentStatus.Signed;

  return (
    <Container py="lg" w="100%">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>
            {i18n.language === 'fr'
              ? consent.templateNameFr
              : consent.templateNameEn}
          </Title>
          <Group gap="sm">
            {isFullySigned ? (
              <Badge
                color="green"
                variant="light"
                leftSection={<IconCheck size={12} />}
              >
                {t('eicf.view.signedBadge')}
              </Badge>
            ) : (
              <Badge
                color="yellow"
                variant="light"
                leftSection={<IconClock size={12} />}
              >
                {t('eicf.view.awaitingBadge')}
              </Badge>
            )}
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={() =>
                downloadPdf.mutate({
                  language: i18n.language,
                  patientName: consent.patientName,
                  templateName:
                    i18n.language === 'fr'
                      ? consent.templateNameFr
                      : consent.templateNameEn,
                })
              }
              loading={downloadPdf.isPending}
            >
              {t('eicf.actions.downloadPdf')}
            </Button>
          </Group>
        </Group>
        <Text size="sm" c="dimmed">
          {t('eicf.page.versionLabel', { version: consent.templateVersion })}
        </Text>
        {consent.templateKind === EICFConsentTemplateKind.FillablePdf ? (
          consent.signedPdfUrl ? (
            <iframe
              title={t('eicf.view.signedPdfTitle')}
              src={consent.signedPdfUrl}
              style={{ width: '100%', height: '75vh', border: 'none' }}
            />
          ) : (
            <Text c="dimmed">{t('eicf.view.pdfUnavailable')}</Text>
          )
        ) : (
          <Paper p="md" withBorder>
            {survey ? (
              <SurveyComponent model={survey} />
            ) : (
              <Text c="red">{t('eicf.page.surveyLoadError')}</Text>
            )}
          </Paper>
        )}
      </Stack>
    </Container>
  );
});

export default EICFConsentViewPage;
